import * as i0 from '@angular/core';
import { HostBinding, Input, Directive, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const LIMIT = 99;
/**
 * To use this component in a module, you can import the `CentricNotificationBadgeModule`:
 * @example
 * ```typescript
 * import { CentricNotificationBadgeModule } from '@windmill/ng-windmill/notification-badge';
 * ```
 *
 * To use the standalone component directly, you can import `NotificationBadgeDirective`:
 * @example
 * ```typescript
 * import { NotificationBadgeDirective } from '@windmill/ng-windmill/notification-badge';
 * ```
 */
class NotificationBadgeDirective {
    _elementRef;
    _renderer;
    /**
     * Property used to set the badge value accordingly, considering the validity of the given value
     */
    set badgeValue(value) {
        this._badgeValue = Number(value);
        this.displayedValue = String(this._badgeValue);
        this.hasInvalidValue = false;
        if (Number(value) <= 0 || isNaN(Number(value))) {
            this.hasInvalidValue = true;
            return;
        }
        if (Number(value) > LIMIT) {
            this.displayedValue = '99+';
        }
        this.updateBadgeValue();
    }
    get badgeValue() {
        return this._badgeValue;
    }
    get isSmall() {
        return this._badgeSize === 'small';
    }
    get isMedium() {
        return this._badgeSize === 'medium';
    }
    /**
     * Property used to set the size of the badge
     */
    get badgeSize() {
        return this._badgeSize;
    }
    set badgeSize(size) {
        this._badgeSize = size;
        this.updateBadgeValue();
    }
    /**
     * Property used to set the badge overlap
     */
    set badgeOverlap(value) {
        this._badgeOverlap = value;
    }
    get badgeOverlap() {
        return this._badgeOverlap;
    }
    get isHiddenOrInvalid() {
        return this._hasInvalidValue || this._badgeHidden;
    }
    /**
     * Property used to hide the badge
     */
    set badgeHidden(value) {
        this._badgeHidden = value;
    }
    get badgeHidden() {
        return this._badgeHidden;
    }
    displayedValue = '';
    _badgeOverlap = true;
    _badgeHidden = false;
    _badgeSize = 'medium';
    _badgeValue = 0;
    _hostElement;
    _badgeElement;
    _hasInvalidValue = false;
    constructor(_elementRef, _renderer) {
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._hostElement = _elementRef.nativeElement;
    }
    ngOnInit() {
        this.createBadge();
    }
    ngOnDestroy() {
        this.destroyBadge();
    }
    getBadgeElement() {
        return this._badgeElement;
    }
    createBadge() {
        this._badgeElement = this._renderer.createElement('div');
        this._hostElement.appendChild(this._badgeElement);
        this._hostElement.classList.add('centric-badge-host');
        this._badgeElement.classList.add('centric-badge-element');
        if (Number(this._badgeValue) > 0 && this.displayedValue) {
            this.updateBadgeValue();
        }
    }
    destroyBadge() {
        if (this._hostElement.contains(this._badgeElement)) {
            this._renderer.removeChild(this._hostElement, this._badgeElement);
        }
    }
    updateBadgeValue() {
        if (typeof this._badgeElement === 'undefined') {
            return;
        }
        this._badgeElement.innerText = this.badgeSize === 'small' ? '' : this.displayedValue;
    }
    set hasInvalidValue(value) {
        this._hasInvalidValue = value;
    }
    get hasInvalidValue() {
        return this._hasInvalidValue;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotificationBadgeDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: NotificationBadgeDirective, isStandalone: true, selector: "[centric-badge]", inputs: { badgeValue: "badgeValue", badgeSize: "badgeSize", badgeOverlap: "badgeOverlap", badgeHidden: "badgeHidden" }, host: { properties: { "class.centric-badge-small": "this.isSmall", "class.centric-badge-medium": "this.isMedium", "class.centric-badge-hidden": "this.isHiddenOrInvalid", "class.centric-badge-overlap": "this._badgeOverlap" } }, exportAs: ["centric-badge"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotificationBadgeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[centric-badge]',
                    exportAs: 'centric-badge',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { badgeValue: [{
                type: Input
            }], isSmall: [{
                type: HostBinding,
                args: ['class.centric-badge-small']
            }], isMedium: [{
                type: HostBinding,
                args: ['class.centric-badge-medium']
            }], badgeSize: [{
                type: Input
            }], badgeOverlap: [{
                type: Input
            }], isHiddenOrInvalid: [{
                type: HostBinding,
                args: ['class.centric-badge-hidden']
            }], badgeHidden: [{
                type: Input
            }], _badgeOverlap: [{
                type: HostBinding,
                args: ['class.centric-badge-overlap']
            }] } });

const moduleComponents = [NotificationBadgeDirective];
class CentricNotificationBadgeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricNotificationBadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricNotificationBadgeModule, imports: [CommonModule, NotificationBadgeDirective], exports: [NotificationBadgeDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricNotificationBadgeModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricNotificationBadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricNotificationBadgeModule, NotificationBadgeDirective };
